import React, { useState } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useBuilding } from "../../context/BuildingContext";
import PageHeader from "../../components/common/PageHeader";

// Import settings section components
import GeneralSettings from "./sections/GeneralSettings";
import UserSettings from "./sections/UserSettings";
import NotificationSettings from "./sections/NotificationSettings";
import BillingSettings from "./sections/BillingSettings";
import APISettings from "./sections/APISettings";
import EmailTemplates from "./sections/EmailTemplates";
// import ImportExport from './sections/ImportExport';

const SettingsPage: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const { user } = useUser();

  // Placeholder for settings saving state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean | null>(null);

  // Determine the active section based on the current path
  const activePath = pathname.split("/").pop() || "general";

  // Navigation items configuration
  const navigationItems = [
    {
      id: "general",
      name: "Company",
      path: "/dashboard/settings/general",
      icon: "cog",
    },
    {
      id: "users",
      name: "User Management",
      path: "/dashboard/settings/users",
      icon: "users",
    },
    {
      id: "notifications",
      name: "Notifications",
      path: "/dashboard/settings/notifications",
      icon: "bell",
    },
    {
      id: "billing",
      name: "Billing & Subscription",
      path: "/dashboard/settings/billing",
      icon: "credit-card",
    },
    {
      id: "api",
      name: "API & Integrations",
      path: "/dashboard/settings/api",
      icon: "code",
    },
    {
      id: "templates",
      name: "Email Templates",
      path: "/dashboard/settings/templates",
      icon: "mail",
    },
    {
      id: "import-export",
      name: "Import / Export",
      path: "/dashboard/settings/import-export",
      icon: "database",
    },
  ];

  // Handler for saving settings (to be passed to child components)
  const handleSaveSettings = async (settings: any, section: string) => {
    try {
      setIsSaving(true);
      // In a real app, you would send the settings to your API
      // const token = await getToken() || '';
      // await fetch(`/api/settings/${section}`, {
      //   method: 'PUT',
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify(settings)
      // });

      // Simulate API request delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error(`Error saving ${section} settings:`, error);
      setSaveSuccess(false);
      setTimeout(() => setSaveSuccess(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function for rendering icons
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "cog":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        );
      case "users":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        );
      case "bell":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        );
      case "credit-card":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        );
      case "code":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
            />
          </svg>
        );
      case "mail":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case "database":
        return (
          <svg
            className="mr-3 h-6 w-6 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  // If path is just /dashboard/settings, redirect to /dashboard/settings/general
  React.useEffect(() => {
    if (pathname === "/dashboard/settings") {
      navigate("/dashboard/settings/general", { replace: true });
    }
  }, [pathname, navigate]);

  return (
    <div className="">
      <PageHeader
        title="Settings"
        description={`Configure settings for ${
          currentBuilding?.name || "your building"
        }`}
      />

      {/* Settings saved notification */}
      {saveSuccess !== null && (
        <div
          className={`mt-4 p-4 rounded-md ${
            saveSuccess ? "bg-green-50" : "bg-red-50"
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {saveSuccess ? (
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3
                className={`text-sm font-medium ${
                  saveSuccess ? "text-green-800" : "text-red-800"
                }`}
              >
                {saveSuccess
                  ? "Settings saved successfully"
                  : "Failed to save settings"}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 lg:grid lg:grid-cols-12 lg:gap-x-5">
        {/* Settings navigation */}
        <aside className="py-6 px-2 sm:px-6 lg:py-0 lg:px-0 lg:col-span-3">
          <nav className="space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`group rounded-md px-3 py-2 flex items-center text-sm font-medium ${
                  activePath === item.id
                    ? "bg-primary-50 text-primary-700 hover:text-primary-700 hover:bg-primary-50"
                    : "text-gray-900 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {renderIcon(item.icon)}
                {item.name}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Settings content */}
        <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
          <Routes>
            <Route
              path="general"
              element={
                <GeneralSettings
                  onSave={(settings) => handleSaveSettings(settings, "general")}
                  isSaving={isSaving}
                />
              }
            />
            <Route
              path="users"
              element={
                <UserSettings
                  onSave={(settings) => handleSaveSettings(settings, "users")}
                  isSaving={isSaving}
                />
              }
            />
            <Route
              path="notifications"
              element={
                <NotificationSettings
                  onSave={(settings) =>
                    handleSaveSettings(settings, "notifications")
                  }
                  isSaving={isSaving}
                />
              }
            />
            <Route
              path="billing"
              element={
                <BillingSettings
                  onSave={(settings) => handleSaveSettings(settings, "billing")}
                  isSaving={isSaving}
                />
              }
            />
            <Route
              path="api"
              element={
                <APISettings
                  onSave={(settings) => handleSaveSettings(settings, "api")}
                  isSaving={isSaving}
                />
              }
            />
            <Route
              path="templates"
              element={
                <EmailTemplates
                  onSave={(settings) =>
                    handleSaveSettings(settings, "templates")
                  }
                  isSaving={isSaving}
                />
              }
            />
            {/* <Route path="import-export" element={
              <ImportExport 
                onSave={(settings) => handleSaveSettings(settings, 'import-export')} 
                isSaving={isSaving}
              />
            } /> */}
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
